'use client';
import TestCourseModal from '@/components/test-course-modal';
import { DefaultCallNumber } from '@/data/globals.data';
import { ContactPlacement, PLACEMENT_ATTRIBUTE } from '@/lib/analytics';
import { StoreDomainConfigEntity } from '@/services/types/store.type';
import { useModalStore } from '@/store/modal.store';
import { useSidebarStore } from '@/store/sidebar.store';
import { useTestCourseModalStore } from '@/store/test-course-modal.store';
import { usePathname } from 'next/navigation';
import React from 'react';
import ConsultationForm from './consultation-form';
import ImageOptimizer from './image-optimizer';
import MobileSidebar from './mobile-sidebar';

interface MainLayoutProps {
  children: React.ReactNode;
  storeDomainConfig: StoreDomainConfigEntity;
}

const MainLayout = ({ children, storeDomainConfig }: MainLayoutProps) => {
  const { isOpen, open: openModal } = useModalStore();
  const { isOpen: isTestCourseModalOpen } = useTestCourseModalStore();
  const { isOpen: isSidebarOpen, close: closeSidebar } = useSidebarStore();

  const pathname = usePathname();
  // 하단 바텀시트를 쓰는 화면. 전화·상담 기능이 겹치므로 플로팅을 숨긴다.
  // 시험장 상세(/exam/license-test-course/[slug])에는 바텀시트가 없어 경로를 정확히 비교한다.
  const hasBottomSheet =
    pathname === '/exam/license-test-course' ||
    pathname === '/samples/license-test-course-bottom-sheet';

  // /branch/[branchSlug], /recommend-class, /event/retest-license-fee 경로에서 안 보이도록 설정
  const showMobileFloating =
    !pathname?.startsWith('/branch/') &&
    !pathname?.startsWith('/recommend-class') &&
    !pathname?.startsWith('/event/retest-license-fee') &&
    !hasBottomSheet;

  return (
    <div className="desktop:max-w-screen mobile-w relative mx-auto box-border w-full overflow-hidden bg-white">
      <MobileSidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
      {children}
      {isOpen && <ConsultationForm storeDomainConfig={storeDomainConfig} />}
      {isTestCourseModalOpen && <TestCourseModal />}

      {showMobileFloating && (
        <div
          {...{ [PLACEMENT_ATTRIBUTE]: ContactPlacement.MOBILE_FLOATING }}
          className="desktop:hidden mobile-w pointer-events-none fixed bottom-0 w-full"
        >
          <div className="mobile-w pointer-events-none inline-flex w-full flex-col items-end pr-6 pb-6">
            <a
              href={`tel:${DefaultCallNumber}`}
              className="pointer-events-auto"
            >
              <ImageOptimizer
                src="/images/main/floating_call_btn.png"
                alt="전화상담 버튼"
                width="56"
                height="56"
              />
            </a>
            <a
              href="https://pf.kakao.com/_urbdG/chat"
              target="_blank"
              rel="noopener noreferrer"
              className="pointer-events-auto"
            >
              <ImageOptimizer
                src="/images/main/floating_kakao_btn.png"
                alt="카카오 버튼"
                width="56"
                height="56"
              />
            </a>

            <button
              onClick={() => openModal()}
              className="pointer-events-auto cursor-pointer"
            >
              <ImageOptimizer
                src="/images/main/floating_reserve_btn.png"
                alt="상담예약 버튼"
                width="147"
                height="56"
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
